/**
 * Backend permission codes follow the shape `<verb>_<resource>[:<scope>]` —
 * e.g. `view_call`, `view_call:all`, `edit_sequence:lead`,
 * `manage_integration_deletion_request`. Grouping them by resource makes a
 * long flat list of tags scannable ("what can this key do to `contact`?")
 * instead of forcing the reader to eyeball a wall of similar strings.
 *
 * Parse rule: split on the FIRST underscore into verb + rest, then split the
 * rest on `:` into resource + optional scope. Codes with no underscore fall
 * back to using the whole code as the resource so we never lose information.
 */

/**
 * CRUD-flavoured semantic buckets used to sort action tags in a natural
 * `view → create → edit → delete → admin` reading order.
 */
export type PermissionCategory = 'view' | 'create' | 'edit' | 'delete' | 'admin'

type ParsedPermission = {
  /** e.g. `view`, `create`, `manage` */
  verb: string
  /** e.g. `call`, `sequence`, `integration_deletion_request` */
  resource: string
  /** e.g. `all`, `only_own`, `lead`, `step` — null when the code has no `:` */
  scope: string | null
  /**
   * Human-readable action label rendered on the tag —
   * `view:all` → "View All", `edit:lead` → "Edit Lead", `create` → "Create".
   */
  actionLabel: string
  /** Semantic bucket for sort ordering. */
  category: PermissionCategory
  /** Original permission code — used as the tooltip on each action tag. */
  code: string
}

export type PermissionResourceGroup = {
  /** Raw resource slug — used as the stable group key. */
  resource: string
  /**
   * Human-readable resource label rendered as the row header — e.g. `call`
   * → "Call", `integration_deletion_request` → "Integration Deletion
   * Request". Title-cased-with-spaces to match the Permission matrix look.
   */
  resourceLabel: string
  perms: ParsedPermission[]
}

const categoryByVerb: Record<string, PermissionCategory> = {
  view: 'view',
  create: 'create',
  add: 'create',
  import: 'create',
  edit: 'edit',
  update: 'edit',
  config: 'edit',
  configure: 'edit',
  merge: 'edit',
  delete: 'delete',
  cancel: 'delete',
  manage: 'admin',
  approve: 'admin',
  export: 'admin',
  complete: 'admin'
}

const verbToCategory = (verb: string): PermissionCategory =>
  categoryByVerb[verb] ?? 'admin'

const categoryRank: Record<PermissionCategory, number> = {
  view: 0,
  create: 1,
  edit: 2,
  delete: 3,
  admin: 4
}

/**
 * Simple Title-Case transform used to render underscore-separated backend
 * slugs as readable UI text. Not localised — the credential API does not
 * expose i18n labels here (unlike the roles matrix), so this is the
 * pragmatic fallback until a labelled endpoint exists.
 */
const humanizeSegment = (segment: string): string =>
  segment
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const humanizeAction = (verb: string, scope: string | null): string =>
  scope
    ? `${humanizeSegment(verb)} ${humanizeSegment(scope)}`
    : humanizeSegment(verb)

const parsePermission = (code: string): ParsedPermission => {
  const underscore = code.indexOf('_')
  const verb = underscore === -1 ? code : code.slice(0, underscore)
  const rest = underscore === -1 ? '' : code.slice(underscore + 1)
  const colon = rest.indexOf(':')
  const resource = (colon === -1 ? rest : rest.slice(0, colon)) || code
  const scope = colon === -1 ? null : rest.slice(colon + 1) || null

  return {
    verb,
    resource,
    scope,
    actionLabel: humanizeAction(verb, scope),
    category: verbToCategory(verb),
    code
  }
}

/**
 * Groups a list of permission codes by resource. The outer group list is
 * alphabetical by resource; each inner list is CRUD-ordered so the same
 * credential always renders in the same predictable order.
 */
export const groupPermissionsByResource = (
  codes: readonly string[]
): PermissionResourceGroup[] => {
  const groups = new Map<string, ParsedPermission[]>()
  for (const code of codes) {
    const parsed = parsePermission(code)
    const bucket = groups.get(parsed.resource)
    if (bucket) {
      bucket.push(parsed)
    } else {
      groups.set(parsed.resource, [parsed])
    }
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resource, perms]) => ({
      resource,
      resourceLabel: humanizeSegment(resource),
      perms: perms.slice().sort((a, b) => {
        const rank = categoryRank[a.category] - categoryRank[b.category]
        if (rank !== 0) return rank
        return a.code.localeCompare(b.code)
      })
    }))
}
